import { getAPIRoot, getCachedAccessToken, getCachedAuthUser } from './api';
import { buildAbsoluteUrl } from '../config/domain';
import featureFlags from '../config/featureFlags';

const STORAGE_KEY = 'fs_referral_code';

export const referralStorage = {
  get: () => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  },
  set: (code) => {
    try {
      if (code) localStorage.setItem(STORAGE_KEY, code);
    } catch (_) {}
  },
  clear: () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  }
};

export async function getAuthToken() {
  return getCachedAccessToken();
}

export async function ensureMyReferralCode() {
  if (!featureFlags.referral_attribution_v1) {
    return null;
  }
  const token = await getAuthToken();
  const userId = getCachedAuthUser()?.id || null;
  if (!userId) {
    // Silently fail if not authenticated - referral code not needed for guests
    return null;
  }

  const apiBase = getAPIRoot();
  try {
    const resp = await fetch(`${apiBase}/referral/my-code`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...(userId ? { 'x-user-id': userId } : {}),
        'Accept': 'application/json'
      }
    });
    
    // Handle 404 (feature disabled or endpoint not available) gracefully
    if (resp.status === 404) {
      return null;
    }
    
    if (!resp.ok) {
      const text = await resp.text();
      // Only throw for non-404 errors
      if (resp.status !== 404) {
        throw new Error(`Failed to get referral code: ${resp.status} ${text}`);
      }
      return null;
    }
    return await resp.json();
  } catch (error) {
    // Silently handle network/parse errors - referral code is optional
    console.warn('Failed to load referral code:', error.message);
    return null;
  }
}

export async function claimReferralByStoredCode() {
  if (!featureFlags.referral_attribution_v1) {
    return { ok: false, reason: 'disabled' };
  }
  const code = referralStorage.get();
  if (!code) return { ok: false, reason: 'no_code' };

  const token = await getAuthToken();
  const userId = getCachedAuthUser()?.id || null;
  if (!userId) return { ok: false, reason: 'no_auth' };

  const apiBase = getAPIRoot();
  const resp = await fetch(`${apiBase}/referral/claim`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(userId ? { 'x-user-id': userId } : {}),
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ code })
  });

  if (!resp.ok) {
    const text = await resp.text();
    return { ok: false, reason: `http_${resp.status}`, message: text };
  }
  const json = await resp.json();
  // On success or already-set, clear the stored code so we don't retry endlessly
  referralStorage.clear();
  return { ok: true, data: json };
}

export function buildShareLink(code) {
  const base = buildAbsoluteUrl('/');
  const normalized = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${normalized}/?r=${encodeURIComponent(code)}`;
}
